import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Rating,
  useTheme,
  alpha,
} from '@mui/material';
import { motion } from 'framer-motion';

const TestimonialsSection = () => {
  const theme = useTheme();

  const testimonials = [
    {
      id: 1,
      name: '张梦琪',
      university: '马来亚大学',
      program: '工程学硕士',
      avatar: '/avatars/student1.jpg',
      rating: 5,
      comment: '感谢StudyMalaysia团队的专业指导！从选校到申请，每个环节都有详细的规划。现在我在马来亚大学的学习生活非常充实，专业老师们都很负责任。',
      year: '2024届',
    },
    {
      id: 2,
      name: '李浩然',
      university: '博特拉大学',
      program: '商科本科',
      avatar: '/avatars/student2.jpg',
      rating: 5,
      comment: '整个申请过程很顺利，顾问老师不仅帮我选择了最适合的专业，还协助我准备了所有申请材料。博特拉大学的环境很美，同学们都很友善。',
      year: '2023届',
    },
    {
      id: 3,
      name: '王雨欣',
      university: '国民大学',
      program: '教育学博士',
      avatar: '/avatars/student3.jpg',
      rating: 5,
      comment: '作为博士申请，过程相对复杂，但StudyMalaysia的团队非常专业，帮我联系了导师，准备了研究计划。现在的研究进展很顺利，非常感谢！',
      year: '2023届',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <Box
      sx={{
        py: 8,
        background: `linear-gradient(135deg, 
          ${alpha(theme.palette.primary.main, 0.02)} 0%, 
          ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
      }}
    >
      <Container maxWidth="xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: theme.palette.text.primary,
              }}
            >
              学生成功故事
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: 600,
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.2rem' },
              }}
            >
              听听我们成功学子的真实分享，了解他们的留学体验
            </Typography>
          </Box>

          {/* Testimonials Grid */}
          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={testimonial.id}>
                <motion.div variants={cardVariants}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 3,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                      },
                      '&::before': {
                        content: '"\\201C"',
                        position: 'absolute',
                        top: -10,
                        left: 20,
                        fontSize: '4rem',
                        color: alpha(theme.palette.primary.main, 0.1),
                        fontFamily: 'serif',
                        lineHeight: 1,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 0 }}>
                      {/* Student Info */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Avatar
                          sx={{
                            width: 60,
                            height: 60,
                            mr: 2,
                            backgroundColor: theme.palette.primary.main,
                            fontSize: '1.5rem',
                            fontWeight: 600,
                          }}
                        >
                          {testimonial.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                            }}
                          >
                            {testimonial.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            {testimonial.program}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ 
                              color: theme.palette.primary.main,
                              fontWeight: 500,
                            }}
                          >
                            {testimonial.university}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Rating */}
                      <Box sx={{ mb: 2 }}>
                        <Rating
                          value={testimonial.rating}
                          readOnly
                          size="small"
                          sx={{ color: theme.palette.warning.main }}
                        />
                      </Box>

                      {/* Comment */}
                      <Typography
                        variant="body1"
                        sx={{
                          mb: 3,
                          color: theme.palette.text.secondary,
                          lineHeight: 1.7,
                          fontStyle: 'italic',
                        }}
                      >
                        {testimonial.comment}
                      </Typography>

                      {/* Year */}
                      <Box
                        sx={{
                          display: 'inline-block',
                          px: 2,
                          py: 0.5,
                          borderRadius: 20,
                          backgroundColor: alpha(theme.palette.success.main, 0.1),
                          color: theme.palette.success.main,
                          fontSize: '0.875rem',
                          fontWeight: 600,
                        }}
                      >
                        {testimonial.year}
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default TestimonialsSection;
